/**
 * Thin client for the Discovery chat endpoints.
 *
 * Each helper hits the matching server route under
 * /api/discovery/sessions/:sessionId. They throw on non-2xx so the caller
 * can surface a friendly error in the chat surface.
 */

export interface DiscoveryTurnResponse {
  session: { id: string; currentPhase: number; state: string };
  assistantMessage: string;
  extractedEntities: Record<string, unknown>;
  shouldAdvancePhase: boolean;
  idempotent: boolean;
}

export interface DiscoveryAdvanceResponse {
  session: { id: string; currentPhase: number; state: string };
  currentPhase: number;
}

export interface DiscoveryCompleteResponse {
  session: { id: string; currentPhase: number; state: string };
  draftPlay: unknown;
}

export async function postTurn(
  sessionId: string,
  body: { turnId: string; userText: string },
): Promise<DiscoveryTurnResponse> {
  const r = await fetch(`/api/discovery/sessions/${sessionId}/turns`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ turn_id: body.turnId, user_message: body.userText }),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`turn failed ${r.status}`);
  return (await r.json()) as DiscoveryTurnResponse;
}

export async function advance(
  sessionId: string,
): Promise<DiscoveryAdvanceResponse> {
  const r = await fetch(`/api/discovery/sessions/${sessionId}/advance`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ confirmed_by_user: true }),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`advance failed ${r.status}`);
  return (await r.json()) as DiscoveryAdvanceResponse;
}

export async function complete(
  sessionId: string,
): Promise<DiscoveryCompleteResponse> {
  const r = await fetch(`/api/discovery/sessions/${sessionId}/complete`, {
    method: "POST",
    credentials: "include",
  });
  if (!r.ok) throw new Error(`complete failed ${r.status}`);
  return (await r.json()) as DiscoveryCompleteResponse;
}

export async function revisit(
  sessionId: string,
  phase: number,
): Promise<DiscoveryAdvanceResponse> {
  const r = await fetch(`/api/discovery/sessions/${sessionId}/revisit`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ phase }),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`revisit failed ${r.status}`);
  return (await r.json()) as DiscoveryAdvanceResponse;
}
